import { createContext, ReactNode, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState, useSyncExternalStore } from "react"
import { readVisitorPref, writeVisitorPref } from "@homerunner-next/widget-core/runtime"
import { VisitorFilter } from "./config"
import { CompareProvider, type CompareHost } from "./compare/CompareProvider"

// SSR-safe layout effect: the renderer executes this module server-side, where
// useLayoutEffect warns under renderToString.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect
import { isFlexMonth, isFlexStayLength, type FlexStayLength } from "./components/filters/flexDates"

const noopSubscribe = () => () => {}

// Keys that are numbers in the filter schema
const numericFilterKeys = new Set<keyof VisitorFilter>([
  'adults', 'children', 'guests', 'bedrooms', 'bathrooms', 'price_min', 'price_max',
])

// Keys that are booleans in the filter schema
const booleanFilterKeys = new Set<keyof VisitorFilter>(['pets', 'featured'])

// Keys to exclude from URL sync (transient state or admin-configured)
const excludeFromUrl = new Set<keyof VisitorFilter>(['map_bounds', 'exclude_property'])

// All syncable filter keys
const urlFilterKeys: (keyof VisitorFilter)[] = [
  'checkin', 'checkout', 'adults', 'children', 'pets', 'types', 'amenities',
  'locations', 'cities', 'groups', 'featured', 'keywords', 'sort_by', 'tags',
  'guests', 'bedrooms', 'bathrooms', 'price_min', 'price_max',
]

/**
 * The flexible-dates URL contract, `flex=flexible&stay_length=…&flex_month=…`
 * (#870 slice 6). Byte-identical to the toolkit's (searchbar-pro.js:1341-1369)
 * so a link works in both directions between a WP site running Searchbar Pro
 * and a site running this widget.
 *
 * DELIBERATELY NOT visitor filters: `state.visitorFilters` is handed whole to
 * `useFetchProperties` (ExplorerContent.tsx:32) and to the map, so a key added
 * there becomes a properties-API query param. The server only ever sees the
 * REPRESENTATIVE checkin/checkout a flexible selection resolves to — the flex
 * triple rides the URL purely so the flexible UI can rehydrate itself.
 * It still shares this one writer so the two never race over `location.search`.
 */
const flexUrlKeys = ['flex', 'stay_length', 'flex_month'] as const

export type FlexDatesSelection = { stay: FlexStayLength; month: string }

/**
 * The searched dates a zero-results rescue chip pivoted AWAY from (#1265) —
 * the reference the adjusted-dates notice names ("No homes were available for
 * X. Showing Y instead."). Module: the `hrtk_adj_from` URL marker
 * (flexible-search.js:594-601). Held in state, not the URL: the pivot happens
 * in place here, and a shared/refreshed link should open as the search it
 * carries, not re-announce an adjustment.
 */
export type AdjustedFrom = { checkin: string; checkout: string }

/**
 * The marker survives a filter change only while it still describes the
 * search: retired when the dates are removed or revert to the searched range
 * (module `syncAdjustedNotice`, flexible-search.js:693-703).
 */
function adjustedFromFor(from: AdjustedFrom | null, filters: Partial<VisitorFilter>): AdjustedFrom | null {
  if (!from) return null
  if (!filters.checkin || !filters.checkout) return null
  if (from.checkin === filters.checkin && from.checkout === filters.checkout) return null
  return from
}

/** A rescue-chip pivot: the window to search and the dates it is an adjustment OF. */
export type RescuePivot = { checkin: string; checkout: string; from: AdjustedFrom | null }

/**
 * Taking a rescue window (#1265), as ONE state transition so no intermediate
 * render can query a half-applied search:
 *   - the window becomes the exact search (a flexible selection is left
 *     behind — module `urlOnlyRedirect` keepFlex=false, flexible-search.js:589-593);
 *   - `from` becomes the adjusted-dates reference (retired at once if it
 *     already equals the new dates).
 * The map is deliberately untouched: the rescue is never offered while an
 * applied search-as-move area narrows the results (`rescueApplies`), so the
 * pivot has no viewport to reconcile and the visitor's map state survives.
 */
export function applyRescuePivot(state: ExplorerState, pivot: RescuePivot): ExplorerState {
  const visitorFilters = { ...state.visitorFilters, checkin: pivot.checkin, checkout: pivot.checkout }
  return {
    ...state,
    visitorFilters,
    flexDates: null,
    adjustedFrom: adjustedFromFor(pivot.from, visitorFilters),
  }
}

function parseFlexFromUrl(): FlexDatesSelection | null {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  if (params.get('flex') !== 'flexible') return null
  const stay = params.get('stay_length')
  if (!isFlexStayLength(stay)) return null
  // A missing or malformed month degrades to Anytime rather than dropping the
  // whole selection — the reference's own fallback (searchbar-pro.js:1350-1354).
  const rawMonth = params.get('flex_month')
  return { stay, month: isFlexMonth(rawMonth) ? rawMonth : 'anytime' }
}

function parseFiltersFromUrl(): Partial<VisitorFilter> {
  if (typeof window === 'undefined') return {}
  const params = new URLSearchParams(window.location.search)
  const filters: Partial<VisitorFilter> = {}

  for (const key of urlFilterKeys) {
    const value = params.get(key)
    if (value === null || value === '') continue

    if (numericFilterKeys.has(key)) {
      const num = Number(value)
      if (!isNaN(num)) (filters as Record<string, unknown>)[key] = num
    } else if (booleanFilterKeys.has(key)) {
      // 'yes' is the module's canonical pets value (searchbar-pro.js:17,
      // pets=yes|no) — inbound module-era links must hydrate (#932); the
      // widget's own outbound links keep true/1.
      const truthy = value === 'true' || value === '1' || (key === 'pets' && value === 'yes')
      if (key === 'pets') {
        // Applied pets is true-or-absent, never a present false: a canonical
        // pets=no link must not read as an applied Guests value or ride the
        // properties request as pets=false (the serializer only skips
        // undefined/null/''). An EXPLICIT off token becomes a present
        // UNDEFINED — it wins the merge over an initialFilters pets=true
        // while every consumer treats it as absent. Junk is ignored so a
        // malformed value never clears a configured default.
        if (truthy) (filters as Record<string, unknown>)[key] = true
        else if (value === 'no' || value === 'false' || value === '0') (filters as Record<string, unknown>)[key] = undefined
      } else {
        (filters as Record<string, unknown>)[key] = truthy
      }
    } else {
      (filters as Record<string, unknown>)[key] = value
    }
  }

  return filters
}

/**
 * @param ownsFlex Whether the widget has ever held a flexible selection on this
 *   page load. The 19 `urlFilterKeys` are unconditionally ours, but `flex` /
 *   `stay_length` / `flex_month` are generic enough that a host page can be
 *   using them for something else entirely — and this sync runs on mount, so an
 *   unconditional delete loop stripped a host page's own keys on a widget that
 *   had never touched them. Latched rather than gated on the feature flag: that
 *   would still strip them on a site where flexible dates ARE enabled but the
 *   visitor has not opened the picker.
 */
function syncFiltersToUrl(
  filters: Partial<VisitorFilter>,
  flex: FlexDatesSelection | null,
  ownsFlex: boolean,
) {
  if (typeof window === 'undefined') return
  const params = new URLSearchParams(window.location.search)

  // Remove all filter keys first
  for (const key of urlFilterKeys) {
    params.delete(key)
  }
  // …and the flex triple, so switching back to exact dates clears it from the
  // URL instead of leaving a stale selection to rehydrate on the next load —
  // but only once the widget has actually owned those keys.
  if (ownsFlex) {
    for (const key of flexUrlKeys) {
      params.delete(key)
    }
  }

  // Add current filter values
  for (const [key, value] of Object.entries(filters)) {
    if (excludeFromUrl.has(key as keyof VisitorFilter)) continue
    if (value !== undefined && value !== null && value !== '' && value !== false && value !== 0) {
      params.set(key, String(value))
    }
  }

  if (flex) {
    params.set('flex', 'flexible')
    params.set('stay_length', flex.stay)
    params.set('flex_month', flex.month)
  }

  const qs = params.toString()
  const newUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`
  window.history.replaceState(null, '', newUrl)
}

export type ExplorerState = {
  visitorFilters: Partial<VisitorFilter>
  /** The applied flexible-dates selection, or null for an exact-date search. */
  flexDates: FlexDatesSelection | null
  /** The dates a zero-results rescue pivoted away from, or null (see AdjustedFrom). */
  adjustedFrom: AdjustedFrom | null
  /**
   * Pets stepper count (#932) — presentation state over the boolean
   * `visitorFilters.pets`, the module's own shape: its guest picker steps
   * pets 0-3 but the canonical search form collapses to pets=yes|no
   * (searchbar-pro.js:17, 2836), and neither side round-trips the count
   * through the URL. Shared here (not GuestFilter-local) because the pro
   * variant mounts desktop and mobile pickers side by side and the applied
   * labels ("2 pets") render outside the picker. Invariant, kept by the
   * reducer: petsCount > 0 ⟺ visitorFilters.pets === true.
   */
  petsCount: number
  mapVisible: boolean
  mapSearchActive: boolean
  mapBounds: { north: number; south: number; east: number; west: number } | null
  /**
   * The map-first sheet layout owns the viewport (#856). Search-as-move is
   * SUSPENDED there — the toolkit hides the control — so the bounds must stop
   * FILTERING while it is on, without being cleared: rotating back to the list
   * layout has to restore the visitor's area exactly. Lives in state because
   * every bounds consumer (grid, slider, totals, price histogram) has to agree
   * with the renderers about it.
   */
  mobileSheetActive: boolean
  resetKey: number
}

/** Module BOUNDS.pets (searchbar-pro.js:171). */
export const PETS_MAX = 3

type ExplorerAction =
  | { type: 'SET_FILTER'; payload: Partial<VisitorFilter> }
  | { type: 'SET_PETS_COUNT'; payload: number }
  | { type: 'SET_FLEX_DATES'; payload: FlexDatesSelection | null }
  | { type: 'SET_ADJUSTED_FROM'; payload: AdjustedFrom | null }
  | { type: 'RESCUE_PIVOT'; payload: RescuePivot }
  | { type: 'RESET_FILTERS'; defaults?: Partial<VisitorFilter> }
  | { type: 'TOGGLE_MAP' }
  | { type: 'SET_MAP_VISIBLE'; payload: boolean }
  | { type: 'SET_MAP_BOUNDS'; payload: ExplorerState['mapBounds'] }
  | { type: 'SET_MAP_SEARCH_ACTIVE'; payload: boolean }
  | { type: 'SET_MOBILE_SHEET_ACTIVE'; payload: boolean }
  | { type: 'SEED_FROM_URL'; payload: { filters: Partial<VisitorFilter>; flex: FlexDatesSelection | null } }

function explorerReducer(state: ExplorerState, action: ExplorerAction): ExplorerState {
  switch (action.type) {
    case 'SET_FILTER': {
      // Any write to the pets boolean re-anchors the count: true keeps a
      // positive count (or floors at 1 — the module's own yes→1 hydrate,
      // searchbar-pro.js:4048-4058), false/undefined zeroes it. Writers that
      // know the exact count dispatch SET_PETS_COUNT first; this keeps the
      // invariant for the ones that don't (URL seeds, external clears).
      let petsCount = state.petsCount
      if ('pets' in action.payload) {
        petsCount = action.payload.pets ? Math.max(1, state.petsCount) : 0
      }
      const visitorFilters = { ...state.visitorFilters, ...action.payload }
      return { ...state, visitorFilters, petsCount, adjustedFrom: adjustedFromFor(state.adjustedFrom, visitorFilters) }
    }
    case 'SET_PETS_COUNT': {
      // Atomic: the action moves the boolean with the count, so the public
      // setPetsCount can never break the invariant on its own — callers that
      // follow up with SET_FILTER (commitParty) just re-write the same value.
      const petsCount = Math.max(0, Math.min(PETS_MAX, action.payload))
      const pets = petsCount > 0 ? true : undefined
      const visitorFilters = state.visitorFilters.pets === pets
        ? state.visitorFilters
        : { ...state.visitorFilters, pets }
      return { ...state, petsCount, visitorFilters }
    }
    case 'SET_FLEX_DATES':
      // A flexible selection is a refinement, never an adjustment (module
      // js:680-681): applying one retires any adjusted-dates marker.
      return { ...state, flexDates: action.payload, adjustedFrom: action.payload ? null : state.adjustedFrom }
    case 'SET_ADJUSTED_FROM':
      return { ...state, adjustedFrom: adjustedFromFor(action.payload, state.visitorFilters) }
    case 'RESCUE_PIVOT':
      return applyRescuePivot(state, action.payload)
    case 'RESET_FILTERS':
      // Clear all drops the flexible selection with the dates it produced —
      // leaving it behind would keep the suggested-dates strip on screen
      // advertising a search that is no longer applied.
      return { ...state, visitorFilters: action.defaults ?? {}, petsCount: action.defaults?.pets ? 1 : 0, flexDates: null, adjustedFrom: null, mapBounds: null, resetKey: state.resetKey + 1 }
    case 'TOGGLE_MAP':
      return { ...state, mapVisible: !state.mapVisible, mapBounds: !state.mapVisible ? null : state.mapBounds }
    case 'SET_MAP_VISIBLE':
      // Mirrors TOGGLE_MAP's bounds semantics: opening the map clears stale
      // bounds so they can't filter the fresh view.
      if (action.payload === state.mapVisible) return state
      return { ...state, mapVisible: action.payload, mapBounds: action.payload ? null : state.mapBounds }
    case 'SET_MAP_BOUNDS':
      return { ...state, mapBounds: action.payload }
    case 'SET_MAP_SEARCH_ACTIVE':
      return { ...state, mapSearchActive: action.payload }
    case 'SET_MOBILE_SHEET_ACTIVE':
      // Deliberately does NOT touch mapSearchActive/mapBounds — suspend, do
      // not tear down, so rotating back restores the visitor's area.
      return state.mobileSheetActive === action.payload
        ? state
        : { ...state, mobileSheetActive: action.payload }
    case 'SEED_FROM_URL': {
      // The hydration path's deferred URL read (#1799), as ONE transition: the
      // link lands over the mount state the way SET_FILTER + SET_FLEX_DATES
      // would (URL wins over the defaults, pets re-anchors the stepper). A link
      // that changes no value — none of our keys, or only the defaults the URL
      // sync wrote on an earlier load — changes nothing. One that changes a
      // FILTER also bumps resetKey, because several field nodes copy the
      // filters into local state at mount (the legacy date and guests inputs)
      // and would keep showing the pre-seed values — the same remount Start
      // Over uses.
      //
      // A flex-only link does NOT bump it. No field copies the flexible
      // selection at mount: the pro trigger renders the applied state, and the
      // picker re-seeds its draft when it opens. Bumping there remounted the
      // whole bar for nothing — on a widget with flexible dates switched off
      // (`flexEnabled = pro && flexCfg.enabled`, DateFilter.tsx:345) the
      // selection is not even read, so an inbound `flex` triple repainted
      // nothing and still threw the server's field nodes away.
      const { filters, flex } = action.payload
      const changesFilters = (Object.keys(filters) as (keyof VisitorFilter)[])
        .some((key) => !Object.is(filters[key], state.visitorFilters[key]))
      const changesFlex = flex?.stay !== state.flexDates?.stay || flex?.month !== state.flexDates?.month
      if (!changesFilters && !changesFlex) return state
      const seeded = explorerReducer(
        explorerReducer(state, { type: 'SET_FILTER', payload: filters }),
        { type: 'SET_FLEX_DATES', payload: flex },
      )
      return { ...seeded, resetKey: changesFilters ? seeded.resetKey + 1 : seeded.resetKey }
    }
    default:
      return state
  }
}

export type MapActions = {
  focusMarker: (propertyId: string) => void
  blurMarker: (propertyId: string) => void
  fitBounds: () => void
}

interface ExplorerContextValue {
  state: ExplorerState
  setFilter: (filters: Partial<VisitorFilter>) => void
  setPetsCount: (count: number) => void
  setFlexDates: (flex: FlexDatesSelection | null) => void
  setAdjustedFrom: (from: AdjustedFrom | null) => void
  rescuePivot: (pivot: RescuePivot) => void
  resetFilters: () => void
  toggleMap: () => void
  setMapBounds: (bounds: ExplorerState['mapBounds']) => void
  setMapSearchActive: (active: boolean) => void
  setMobileSheetActive: (active: boolean) => void
  registerMapActions: (actions: MapActions | null) => void
  mapActions: MapActions | null
}

const ExplorerContext = createContext<ExplorerContextValue | undefined>(undefined)

export function ExplorerProvider({
  children,
  initialFilters,
  initialMapVisible = false,
  ignoreUrlFilters = false,
  visitorPrefsKey = null,
  compare = null,
}: {
  children: ReactNode
  initialFilters?: Partial<VisitorFilter>
  initialMapVisible?: boolean
  /**
   * Storage scope for visitor-made toggle persistence (map show/hide via
   * widget-core visitor-prefs, 72h sliding TTL). Null disables persistence
   * entirely: the playground preview host passes null so live-preview toggles
   * never pollute the visitor store, and widget.tsx omits the key when the
   * map is disabled.
   */
  visitorPrefsKey?: string | null
  /**
   * Skip seeding state from the page URL. Set in the playground preview host,
   * where the URL is written solely by our own sync and live edits remount the
   * provider: re-parsing that self-written URL let it win over freshly-changed
   * defaults and latched the old value forever (#837 — un-checking "Featured
   * Only" never refreshed). Real embed pages keep URL precedence so visitor
   * deep links work.
   */
  ignoreUrlFilters?: boolean
  /**
   * Compare Homes (#1713): the widget's settings, handed down by widget.tsx so
   * the compare state can live in this provider's tree. Omitted (the default)
   * means no compare, which is how every direct mount of this provider behaves.
   */
  compare?: CompareHost | null
}) {
  const initialFiltersRef = useRef(initialFilters)

  // The server never sees the page query — the worker keys the listings page
  // by platform only (#650) — so a hydration render must not read it either,
  // or its first render differs from the server HTML ("Add dates" vs
  // "Nov 2 – Nov 5") and React throws the server tree away (#1799). React
  // itself tells us which render this is: useSyncExternalStore hands the
  // server snapshot to the server render AND to the hydration render, the
  // client snapshot to everything else. A createRoot mount has no server HTML
  // to match, so it keeps reading the URL in its first render: one properties
  // request, already filtered. A hydration mount reads it in the layout
  // effect below instead.
  const isHydratingRender = useSyncExternalStore(noopSubscribe, () => false, () => true)
  const readUrlNow = !ignoreUrlFilters && !isHydratingRender
  const [urlSeedPending, setUrlSeedPending] = useState(!ignoreUrlFilters && isHydratingRender)

  const [state, dispatch] = useReducer(explorerReducer, undefined, () => {
    const urlFilters = readUrlNow ? parseFiltersFromUrl() : {}
    const merged = { ...initialFilters, ...urlFilters }
    return {
      visitorFilters: merged,
      // The URL carries only the boolean, so a deep link seeds the stepper at
      // exactly 1 — the module's hydrate does the same (yes → 1).
      petsCount: merged.pets ? 1 : 0,
      // There is no admin-configured flexible selection to fall back to — it is
      // a visitor gesture only — so `ignoreUrlFilters` leaves it simply unset.
      flexDates: readUrlNow ? parseFlexFromUrl() : null,
      adjustedFrom: null,
      mapVisible: initialMapVisible,
      mapSearchActive: false,
      mapBounds: null,
      mobileSheetActive: false,
      resetKey: 0,
    }
  })

  // Ownership latch for the flex triple — see `syncFiltersToUrl`. Flips the
  // first time a flexible selection exists (a valid deep link latches at mount;
  // a visitor who picks flexible then reverts latches when `flexDates` first
  // goes non-null) and never flips back, so clearing back to exact dates still
  // strips the keys. Residual, accepted: once a visitor has actually used the
  // picker the widget owns those keys for the rest of the page load, exactly
  // like the 19 keys that already behave that way.
  const flexOwnedRef = useRef(false)

  // Hydration's deferred URL read (#1799, see `isHydratingRender`): after the
  // first commit, before paint — the map-preference adoption below is the same
  // pattern. Once only.
  useIsomorphicLayoutEffect(() => {
    if (!urlSeedPending) return
    dispatch({ type: 'SEED_FROM_URL', payload: { filters: parseFiltersFromUrl(), flex: parseFlexFromUrl() } })
    setUrlSeedPending(false)
  }, [urlSeedPending])

  // Sync filters to URL whenever they change. Held while the hydration seed is
  // pending: the sync deletes every filter key the state does not hold, so a
  // run on the pre-seed state would erase the deep link from the address bar.
  useEffect(() => {
    if (urlSeedPending) return
    if (state.flexDates) flexOwnedRef.current = true
    syncFiltersToUrl(state.visitorFilters, state.flexDates, flexOwnedRef.current)
  }, [state.visitorFilters, state.flexDates, urlSeedPending])

  const setFilter = useCallback((filters: Partial<VisitorFilter>) => {
    dispatch({ type: 'SET_FILTER', payload: filters })
  }, [])

  const setPetsCount = useCallback((count: number) => {
    dispatch({ type: 'SET_PETS_COUNT', payload: count })
  }, [])

  const setFlexDates = useCallback((flex: FlexDatesSelection | null) => {
    dispatch({ type: 'SET_FLEX_DATES', payload: flex })
  }, [])

  const setAdjustedFrom = useCallback((from: AdjustedFrom | null) => {
    dispatch({ type: 'SET_ADJUSTED_FROM', payload: from })
  }, [])

  const rescuePivot = useCallback((pivot: RescuePivot) => {
    dispatch({ type: 'RESCUE_PIVOT', payload: pivot })
  }, [])

  const resetFilters = useCallback(() => {
    dispatch({ type: 'RESET_FILTERS', defaults: initialFiltersRef.current })
  }, [])

  // Adopt a persisted visitor map choice AFTER the initial commit — seeding
  // the reducer init from storage would desync SSR hydration (client tree ≠
  // server markup regenerates the whole SSR DOM). Pre-paint on the client, so
  // CSR mounts never flash the owner default first.
  useIsomorphicLayoutEffect(() => {
    if (!visitorPrefsKey) return
    const stored = readVisitorPref<boolean>(visitorPrefsKey, 'mapVisible')
    if (typeof stored === 'boolean') dispatch({ type: 'SET_MAP_VISIBLE', payload: stored })
  }, [visitorPrefsKey])

  // Write on explicit visitor gesture ONLY — the adoption dispatch above and
  // the owner-default init must never stamp the store, or a later owner
  // default change would lose to a "choice" the visitor never made.
  const mapVisibleRef = useRef(state.mapVisible)
  mapVisibleRef.current = state.mapVisible

  const toggleMap = useCallback(() => {
    if (visitorPrefsKey) writeVisitorPref(visitorPrefsKey, 'mapVisible', !mapVisibleRef.current)
    dispatch({ type: 'TOGGLE_MAP' })
  }, [visitorPrefsKey])

  const setMapBounds = useCallback((bounds: ExplorerState['mapBounds']) => {
    dispatch({ type: 'SET_MAP_BOUNDS', payload: bounds })
  }, [])

  const setMobileSheetActive = useCallback((active: boolean) => {
    dispatch({ type: 'SET_MOBILE_SHEET_ACTIVE', payload: active })
  }, [])

  const setMapSearchActive = useCallback((active: boolean) => {
    dispatch({ type: 'SET_MAP_SEARCH_ACTIVE', payload: active })
  }, [])

  const mapActionsRef = useRef<MapActions | null>(null)
  const [mapActionsVersion, setMapActionsVersion] = useState(0)

  const registerMapActions = useCallback((actions: MapActions | null) => {
    mapActionsRef.current = actions
    setMapActionsVersion(v => v + 1)
  }, [])

  const ctxValue = useMemo(() => ({
    state, setFilter, setPetsCount, setFlexDates, setAdjustedFrom, rescuePivot, resetFilters, toggleMap, setMapBounds, setMapSearchActive, setMobileSheetActive, registerMapActions,
    mapActions: mapActionsRef.current,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [state, setFilter, setPetsCount, setFlexDates, setAdjustedFrom, rescuePivot, resetFilters, toggleMap, setMapBounds, setMapSearchActive, setMobileSheetActive, registerMapActions, mapActionsVersion])

  return (
    <ExplorerContext.Provider value={ctxValue}>
      {/* Compare Homes (#1713): its picks live in this provider's tree, so filter changes,
          load-more and re-renders keep them. Renders nothing of its own. */}
      <CompareProvider visitorFilters={state.visitorFilters} host={compare}>
        {children}
      </CompareProvider>
    </ExplorerContext.Provider>
  )
}

export function useExplorer() {
  const context = useContext(ExplorerContext)
  if (!context) {
    throw new Error('useExplorer must be used within an ExplorerProvider')
  }
  return context
}
